import React, { useState, useEffect, useMemo, useRef } from "react";
import { useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { QuotationFormValues } from "../initialValues";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import AddCustomerModal from "../Components/AddCustomerModal";
import { fetchCompanyByDropDown, fetchCompanyById } from "slices/Masters/company/thunk";
import { AppDispatch } from "index";
import { showConfirm } from "helpers/swalHelper";
// Add useParams to imports at the top
import { useParams } from "react-router-dom";

// Inside the CustomerDetailsSection component, add after line 22:


const CustomerDetailsSection: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { values, setFieldValue, errors, touched } = useFormikContext<QuotationFormValues | ServiceQuotationFormValues>();
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  
  // Update line 428:
  const disabled = Boolean(values.enquiry_reference_number || (values as any).has_order || (values as any).sales_order_service_id);
  // Selector for company dropdown data
  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({
      companiesData: company.companyList || [],
    })
  );

  const { companiesData } = useSelector(selectCompanyData);

  // Fetch company dropdown data on component mount
  useEffect(() => {
    dispatch(fetchCompanyByDropDown());
  }, [dispatch]);

  // Initialize selectedContactIds when contact_persons change (for edit mode)
  useEffect(() => {
    if (values.contact_persons && values.contact_persons.length > 0) {
      const contactIds = values.contact_persons.map((contact: any) => contact.id).filter(Boolean);
      setSelectedContactIds(contactIds);
    } else {
      setSelectedContactIds([]);
    }
  }, [values.contact_persons]);

  // Track latest contact persons to avoid race conditions with enquiry auto-population
  const latestContactPersonsRef = useRef(values.contact_persons);
  useEffect(() => {
    latestContactPersonsRef.current = values.contact_persons;
  }, [values.contact_persons]);

  // Fetch latest company details on initial mount if in edit mode (has company and contacts)
  useEffect(() => {
    const fetchLatestCompanyDetails = async () => {
      if (values.company?.company_id) {
        try {
          const response = await dispatch(fetchCompanyById(values.company.company_id));
          const payload = (response as any)?.payload;
          const companyData = payload?.data || payload?.result || (payload && payload.company_id ? payload : null);

          if (companyData) {
            // Transform contacts to match expected format
            const contacts = (companyData.contacts || []).map((contact: any) => ({
              contact_id: contact.contact_id,
              name: contact.name,
              designation: contact.designation,
              phone_country: contact.phone_country,
              phone_number: contact.phone_number,
              email: contact.email,
            }));

            console.log("CompanyData",companyData)

            // Update company with latest data (preserve existing structure)
            setFieldValue("company", {
              ...values.company,
              vendor_code: companyData.vendor_code,
              branch_name: companyData.branch_name,
              contacts: contacts,
              industry: companyData.industry || values.company.industry || "",
              gst_type: companyData.gst_type || values.company.gst_type || "",
              company_email: companyData.company_email || values.company.company_email || "",
              phone_number: companyData.phone_number || values.company.phone_number || "",
              gstin: companyData.gstin || values.company.gstin || "",
              billing_address: companyData.billing_address || values.company.billing_address || {},
              shipping_addresses: companyData.shippingAddresses || companyData.shipping_addresses || values.company.shipping_addresses || [],
            });

            // Update customer_details if it exists (preserve quotation-selected contacts)
            if (values.customer_details) {
              setFieldValue("customer_details", {
                ...values.customer_details,
                vendor_code: companyData.vendor_code,
                branch_name: companyData.branch_name,
                industry: companyData.industry || values.customer_details.industry || "",
                gst_type: companyData.gst_type || values.customer_details.gst_type || "",
                company_email: companyData.company_email || values.customer_details.company_email || "",
                phone_number: companyData.phone_number || values.customer_details.phone_number || "",
                gstin: companyData.gstin || values.customer_details.gstin || "",
                billing_address: companyData.billing_address || values.customer_details.billing_address || {},
                shipping_addresses: companyData.shippingAddresses || companyData.shipping_addresses || values.customer_details.shipping_addresses || [],
              });
            }

            // Update existing contact persons with latest data if they still exist
            // Use latest ref to avoid overwriting contacts just set by an enquiry change
            const currentContactPersons = latestContactPersonsRef.current || [];

            if (currentContactPersons.length > 0) {
              const updatedContactPersons = currentContactPersons.map((existingContact: any) => {
                const existingId = String(existingContact.id || existingContact.contact_id || "").trim();
                const updatedContact = contacts.find((c: any) =>
                  String(c.contact_id || c.id || "").trim() === existingId
                );

                if (updatedContact) {
                  return {
                    id: updatedContact.contact_id,
                    name: updatedContact.name,
                    designation: updatedContact.designation,
                    phone_country: updatedContact.phone_country,
                    phone_number: updatedContact.phone_number,
                    email: updatedContact.email,
                  };
                }
                return existingContact; // Keep existing if not found in updated contacts
              }).filter((c: any) => c); // Remove any null/undefined entries

              setFieldValue("contact_persons", updatedContactPersons);
              setSelectedContactIds(updatedContactPersons.map((c: any) => c.id).filter(Boolean));

              if (values.customer_details) {
                const syncedContacts = updatedContactPersons.map((c: any) => ({
                  contact_id: c.id || c.contact_id,
                  name: c.name || c.contact_person_name || "",
                  designation: c.designation,
                  phone_country: c.phone_country,
                  phone_number: c.phone_number,
                  email: c.email,
                }));
                setFieldValue("customer_details", {
                  ...values.customer_details,
                  contacts: syncedContacts,
                  contact_details: syncedContacts,
                });
              }
            }
          }
        } catch (error) {
          console.error("Error fetching latest company details:", error);
        }
      }
    };

    fetchLatestCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.company?.company_id]); // Run when company_id changes (e.g. from enquiry selection)

  // Use API company data directly, but include the currently selected company if it's not in the list
  let companyOptions = Array.isArray(companiesData) ? companiesData : [];

  // Include the currently selected company in options if it's not already there
  if (values.company && !companyOptions.some(opt => opt.company_id === values.company!.company_id)) {
    companyOptions = [...companyOptions, values.company];
  }


  const handleCompanyChange = async (_: any, newValue: any) => {
    if (newValue) {
      // Check if changing company and there are existing products/services
      const hasProducts = (values as any).products && (values as any).products.length > 0;
      const hasServices = (values as any).services && (values as any).services.length > 0;

      // Check if we're changing to a different company
      const isChangingCompany = values.company && values.company.company_id !== newValue.company_id;

      if (isChangingCompany && (hasProducts || hasServices)) {
        const confirmed = await showConfirm(
          "Change Customer?",
          "Changing the customer will clear all products and services. Do you want to continue?",
          "Yes, change customer",
          "Cancel"
        );

        if (!confirmed.isConfirmed) {
          return; // User cancelled, don't change company
        }

        // Clear products and services
        if (hasProducts) {
          setFieldValue("products", []);
        }
        if (hasServices) {
          setFieldValue("services", []);
        }
      }

      // Preserve existing selected contacts if it's the same company (edit mode)
      const existingContactIds = isChangingCompany ? [] : selectedContactIds;
      const existingContactPersons = isChangingCompany ? [] : values.contact_persons;

      // Fetch latest company details to get updated contacts
      try {
        const response = await dispatch(fetchCompanyById(newValue.company_id));
        const payload = (response as any)?.payload;
        const companyData = payload?.data || payload?.result || (payload && payload.company_id ? payload : null);

        if (companyData) {
          // Transform contacts to match expected format
          const contacts = (companyData.contacts || []).map((contact: any) => ({
            contact_id: contact.contact_id,
            name: contact.name,
            designation: contact.designation,
            phone_country: contact.phone_country,
            phone_number: contact.phone_number,
            email: contact.email,
          }));

          // Set the company object with latest data
          setFieldValue("company", {
            company_id: companyData.company_id,
            company_name: companyData.company_name,
            industry: companyData.industry || newValue.industry || "",
            gst_type: companyData.gst_type || newValue.gst_type || "",
            company_email: companyData.company_email || newValue.company_email || "",
            phone_number: companyData.phone_number || newValue.phone_number || "",
            gstin: companyData.gstin || newValue.gstin || "",
            billing_address: companyData.billing_address || newValue.billing_address || {},
            contacts: contacts,
            shipping_addresses: companyData.shippingAddresses || companyData.shipping_addresses || newValue.shipping_addresses || newValue.shippingAddresses || [],
          });

          // Set customer_details with complete information
          setFieldValue("customer_details", {
            company_id: companyData.company_id,
            company_name: companyData.company_name,
            company_email: companyData.company_email || newValue.company_email || "",
            phone_number: companyData.phone_number || newValue.phone_number || "",
            gstin: companyData.gstin || newValue.gstin || "",
            gst_type: companyData.gst_type || newValue.gst_type || "",
            industry: companyData.industry || newValue.industry || "",
            billing_address: companyData.billing_address || newValue.billing_address || {},
            contacts: contacts,
            shipping_addresses: companyData.shippingAddresses || companyData.shipping_addresses || newValue.shipping_addresses || newValue.shippingAddresses || [],
          });

          // Update contact persons if in edit mode (same company)
          if (!isChangingCompany && existingContactIds.length > 0) {
            // Map existing contact IDs to new contact data
            const updatedContactPersons = existingContactPersons.map((existingContact: any) => {
              const updatedContact = contacts.find((c: any) => c.contact_id === existingContact.id);
              if (updatedContact) {
                return {
                  id: updatedContact.contact_id,
                  name: updatedContact.name,
                  designation: updatedContact.designation,
                  phone_country: updatedContact.phone_country,
                  phone_number: updatedContact.phone_number,
                  email: updatedContact.email,
                };
              }
              return existingContact; // Keep existing if not found in updated contacts
            }).filter((c: any) => c); // Remove any null/undefined entries

            setFieldValue("contact_persons", updatedContactPersons);
            setSelectedContactIds(updatedContactPersons.map((c: any) => c.id).filter(Boolean));
          } else {
            // Clear selected contact persons when company changes
            setFieldValue("contact_persons", []);
            setSelectedContactIds([]);
          }
        } else {
          // Fallback to dropdown data if API call fails
          setFieldValue("company", {
            company_id: newValue.company_id,
            company_name: newValue.company_name,
            industry: newValue.industry || "",
            gst_type: newValue.gst_type || "",
            company_email: newValue.company_email || "",
            phone_number: newValue.phone_number || "",
            gstin: newValue.gstin || "",
            billing_address: newValue.billing_address || {},
            contacts: newValue.contacts || [],
            shipping_addresses: newValue.shipping_addresses || newValue.shippingAddresses || [],
          });

          setFieldValue("customer_details", {
            company_id: newValue.company_id,
            company_name: newValue.company_name,
            company_email: newValue.company_email || "",
            phone_number: newValue.phone_number || "",
            gstin: newValue.gstin || "",
            gst_type: newValue.gst_type || "",
            industry: newValue.industry || "",
            billing_address: newValue.billing_address || {},
            contacts: newValue.contacts || [],
            shipping_addresses: newValue.shipping_addresses || newValue.shippingAddresses || [],
          });

          if (isChangingCompany) {
            setFieldValue("contact_persons", []);
            setSelectedContactIds([]);
          }
        }
      } catch (error) {
        console.error("Error fetching company details:", error);
        // Fallback to dropdown data
        setFieldValue("company", {
          company_id: newValue.company_id,
          company_name: newValue.company_name,
          industry: newValue.industry || "",
          gst_type: newValue.gst_type || "",
          company_email: newValue.company_email || "",
          phone_number: newValue.phone_number || "",
          gstin: newValue.gstin || "",
          billing_address: newValue.billing_address || {},
          contacts: newValue.contacts || [],
          shipping_addresses: newValue.shipping_addresses || newValue.shippingAddresses || [],
        });

        setFieldValue("customer_details", {
          company_id: newValue.company_id,
          company_name: newValue.company_name,
          company_email: newValue.company_email || "",
          phone_number: newValue.phone_number || "",
          gstin: newValue.gstin || "",
          gst_type: newValue.gst_type || "",
          industry: newValue.industry || "",
          billing_address: newValue.billing_address || {},
          contacts: newValue.contacts || [],
          shipping_addresses: newValue.shipping_addresses || newValue.shippingAddresses || [],
        });

        if (isChangingCompany) {
          setFieldValue("contact_persons", []);
          setSelectedContactIds([]);
        }
      }
    } else {
      // Customer is being removed
      const hasProducts = (values as any).products && (values as any).products.length > 0;
      const hasServices = (values as any).services && (values as any).services.length > 0;

      if (hasProducts || hasServices) {
        const confirmed = await showConfirm(
          "Remove Customer?",
          "Removing the customer will clear all products and services. Do you want to continue?",
          "Yes, remove customer",
          "Cancel"
        );

        if (!confirmed.isConfirmed) {
          return; // User cancelled, don't remove company
        }

        // Clear products and services
        if (hasProducts) {
          setFieldValue("products", []);
        }
        if (hasServices) {
          setFieldValue("services", []);
        }
      }

      setFieldValue("company", null);
      setFieldValue("customer_details", null);
      setFieldValue("contact_persons", []);
      setSelectedContactIds([]);
    }
  };

  const handleContactRemove = (contactId: string) => {
    setSelectedContactIds(selectedContactIds.filter((id) => id !== contactId));

    // Remove from contact_persons
    const updatedContactPersons = values.contact_persons.filter((c) => c.id !== contactId);
    setFieldValue("contact_persons", updatedContactPersons);

    // Also update customer_details contacts to keep payload in sync
    if (values.customer_details) {
      const syncedContacts = updatedContactPersons.map((c: any) => ({
        contact_id: c.id || c.contact_id,
        name: c.name || c.contact_person_name || "",
        designation: c.designation,
        phone_country: c.phone_country,
        phone_number: c.phone_number,
        email: c.email,
      }));
      setFieldValue("customer_details", {
        ...values.customer_details,
        contacts: syncedContacts,
        contact_details: syncedContacts,
      });
    }
  };

  // Get all contacts for the selected company (for multiselect dropdown)
  const allContacts = (values.company as any)?.contacts
    ?.map((contact: any) => ({
      id: contact.contact_id || contact.id,
      contact_id: contact.contact_id || contact.id,
      contact_person_name: contact.name || contact.contact_name || contact.contact_person_name || "",
      name: contact.name || contact.contact_name || contact.contact_person_name || "",
      designation: contact.designation || contact.contact_person_designation || "",
      phone_country: contact.phone_country || contact.contact_person_phone_country || "",
      phone_number: contact.phone_number || contact.contact_person_phone_no || "",
      email: contact.email || contact.contact_person_email || "",
    })) || [];

  // Get the current selected contacts as an array for the Autocomplete value
  const selectedContactsValue = useMemo(() => {
    return values.contact_persons || [];
  }, [values.contact_persons]);

  // Check if quotation is created from an enquiry
  const isFromEnquiry = Boolean(
    (values as any).enquiry_reference_number &&
    (values as any).enquiry_reference_number.trim() !== ""
  );

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-start align-items-center mb-3 gap-2">
        <h5 className="fw-semibold mb-0 me-1">
          Customer Details
        </h5>
        <Button
          size="sm"
          variant="light"
          className="fw-semibold mb-1 rounded"
          onClick={() => setShowAddCompanyModal(true)}
          disabled={disabled}
          style={{
            backgroundColor: MecordColors?.LightGrayColor,
          }}
        >
          + Add New Customer
        </Button>
      </div>

      <Row
        className="g-3 pb-3 px-1 rounded"
        style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
      >
        <Col xs={12} md={12}>
          <Form.Group>
            <Form.Label>
              Select Company<span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              options={companyOptions}
              getOptionLabel={(option) => option.company_name || ""}
              disabled={disabled}
              isOptionEqualToValue={(option, value) => String(option?.company_id) === String(value?.company_id)}
              value={values.company || null}
              onChange={handleCompanyChange}
              onBlur={() => {
                // Handle blur if needed
              }}
              ListboxProps={{
                sx: {
                  ...autocompleteDropdownStyles.ListboxProps.sx,
                  "& .MuiAutocomplete-option[aria-selected='true']": {
                    backgroundColor: `${MecordColors.ActiveBackground} !important`,
                    color: `${MecordColors.ActiveText} !important`,
                  },
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: `${MecordColors.ActiveBackground} !important`,
                          color: `${MecordColors.ActiveText} !important`,
                        }),
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>{option.company_name}</span>
                        {option.branch_name && (
                          <span
                            style={{
                              fontSize: "0.75rem",
                              color: "#6c757d",
                              marginTop: 2,
                            }}
                          >
                            {option.branch_name}
                          </span>
                        )}
                      </Box>
                      {selected && (
                        <span
                          style={{
                            cursor: "pointer",
                            marginLeft: "10px",
                            color: MecordColors.ActiveText,
                            fontWeight: "bold",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompanyChange(null, null);
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  id="company"
                  name="company"
                  placeholder="Select Company"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
            {errors.company && touched.company && (
              <div className="invalid-feedback d-block">{errors.company as string}</div>
            )}
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>Industry</Form.Label>
            <Form.Control
              type="text"
              value={values.company?.industry || ""}
              placeholder="Industry (Auto-fill)"
              disabled
            // style={{ backgroundColor: "#ffffff" }}
            />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>GST Type</Form.Label>
            <Form.Control
              type="text"
              value={values.company?.gst_type || ""}
              placeholder="GST Type (Auto-fill)"
              disabled
            // style={{ backgroundColor: "#ffffff" }}
            />
          </Form.Group>
        </Col>

        <Col xs={12} sm={6} md={6}>
          <Form.Group>
            <Form.Label>
              Contact Name<span className="text-danger">*</span>
            </Form.Label>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={disabled}
              options={allContacts}
              getOptionLabel={(option) => option.contact_person_name || option.name || ""}
              isOptionEqualToValue={(option, value) => {
                const getContactId = (obj: any) => String(obj?.id || obj?.contact_id || "").trim();
                const optId = getContactId(option);
                const valId = getContactId(value);
                return optId !== "" && optId === valId;
              }}
              value={selectedContactsValue}
              onChange={(_: any, newValue: any) => {
                // Handle the full array of selected contacts
                const updatedContactPersons = Array.isArray(newValue) ? newValue : [];

                // Update selectedContactIds
                const updatedIds = updatedContactPersons.map((c: any) => c.id).filter(Boolean);
                setSelectedContactIds(updatedIds);

                // Update contact_persons
                setFieldValue("contact_persons", updatedContactPersons);

                // Also update customer_details contacts if it exists to keep payload in sync
                if (values.customer_details) {
                  const syncedContacts = updatedContactPersons.map((c: any) => ({
                    contact_id: c.id || c.contact_id,
                    name: c.name || c.contact_person_name || "",
                    designation: c.designation,
                    phone_country: c.phone_country,
                    phone_number: c.phone_number,
                    email: c.email,
                  }));
                  setFieldValue("customer_details", {
                    ...values.customer_details,
                    contacts: syncedContacts,
                    contact_details: syncedContacts,
                  });
                }
              }}
              renderTags={() => null}
              ListboxProps={{
                sx: {
                  ...autocompleteDropdownStyles.ListboxProps.sx,
                  "& .MuiAutocomplete-option[aria-selected='true']": {
                    backgroundColor: `${MecordColors.ActiveBackground} !important`,
                    color: `${MecordColors.ActiveText} !important`,
                  },
                },
              }}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps} style={{ padding: 0 }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.8,
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: MecordColors.Hover,
                          color: MecordColors.ActiveText,
                        },
                        ...(selected && {
                          backgroundColor: `${MecordColors.ActiveBackground} !important`,
                          color: `${MecordColors.ActiveText} !important`,
                        }),
                      }}
                    >
                      <span>{option.contact_person_name || option.name}</span>
                      {selected && (
                        <span
                          style={{
                            cursor: "pointer",
                            marginLeft: "10px",
                            color: MecordColors.ActiveText,
                            fontWeight: "bold",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();

                            const updated = selectedContactsValue.filter(
                              (c: any) => {
                                const optId = String(option?.id || option?.contact_id || "").trim();
                                const currentId = String(c?.id || c?.contact_id || "").trim();
                                return currentId !== optId;
                              }
                            );

                            // Update selectedContactIds
                            const updatedIds = updated.map((c: any) => c.id || c.contact_id).filter(Boolean);
                            setSelectedContactIds(updatedIds);

                            // Update contact_persons
                            setFieldValue("contact_persons", updated);

                            // Also update customer_details.contacts if it exists
                            if (values.customer_details) {
                              const updatedCustomerDetails = {
                                ...values.customer_details,
                                contacts: updated.map((c: any) => ({
                                  contact_id: c.id || c.contact_id,
                                  name: c.name || c.contact_person_name,
                                  designation: c.designation,
                                  phone_country: c.phone_country,
                                  phone_number: c.phone_number,
                                  email: c.email,
                                })),
                              };
                              setFieldValue("customer_details", updatedCustomerDetails);
                            }
                          }}
                        >
                          ✕
                        </span>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  id="contact_persons"
                  name="contact_persons"
                  placeholder="Select Contact Name"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                  error={
                    !!(errors.contact_persons && touched.contact_persons)
                  }
                />
              )}
            />
          </Form.Group>
        </Col>
        {values.contact_persons.length > 0 && (
          <Col xs={12} md={12}>
            <div className="d-flex flex-wrap gap-2">
              {values.contact_persons.map((contact) => (
                <div
                  key={contact.id}
                  className="d-flex align-items-center px-2 py-1 rounded"
                  style={{
                    background: MecordColors?.LightThemeBGColor,
                    border: `1px solid ${MecordColors?.ThemeBGColor}`,
                    fontSize: "14px",
                  }}
                >
                  {contact.name}
                  {!disabled && (
                    <span
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        color: "red",
                      }}
                      onClick={() => handleContactRemove(contact.id)}
                    >
                      ✕
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Col>
        )}
        {errors.contact_persons && touched.contact_persons && (
          <Col xs={12}>
            <div className="invalid-feedback d-block">{errors.contact_persons as string}</div>
          </Col>
        )}
      </Row>

      {/* Add Customer Modal */}
      <AddCustomerModal
        show={showAddCompanyModal}
        onHide={() => setShowAddCompanyModal(false)}
        onSave={(companyData) => {
          // TODO: Implement save logic
          // In a real app, this would make an API call to save the company
          // and then refresh the company options list
          console.log("Company data to save:", companyData);
        }}
      />
    </div>
  );
};

export default CustomerDetailsSection;
